import { sharedFonts } from '../../../assets/shared.styles';

export const authColors = {
  navy: '#0B1220',
  navySoft: '#111827',
  text: '#0F172A',
  muted: '#64748B',
  border: '#E2E8F0',
  surface: '#F8FAFC',
  white: '#FFFFFF',
  primary: '#1D4ED8',
  primaryHover: '#1E40AF',
  primarySoft: '#DBEAFE',
  error: '#DC2626',
  errorSoft: '#FEF2F2',
  green: '#22C55E',
  red: '#EF4444',
  yellow: '#FACC15',
  blue: '#2563EB',
};

export const styles = {
  page: {
    minHeight: '100dvh',
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      md: 'minmax(280px, 44%) minmax(0, 1fr)',
    },
    fontFamily: sharedFonts.main,
    backgroundColor: authColors.surface,
  },
  brandPanel: {
    display: { xs: 'none', md: 'flex' },
    position: 'relative',
    overflow: 'hidden',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: { md: '2.5rem', lg: '3.25rem' },
    color: authColors.white,
    background: `radial-gradient(1200px 600px at -10% -20%, rgba(37, 99, 235, 0.35), transparent 55%),
      radial-gradient(900px 500px at 110% 10%, rgba(34, 197, 94, 0.18), transparent 50%),
      radial-gradient(700px 420px at 70% 110%, rgba(239, 68, 68, 0.16), transparent 50%),
      ${authColors.navy}`,
  },
  brandGlow: {
    position: 'absolute',
    width: 220,
    height: 220,
    borderRadius: '50%',
    filter: 'blur(18px)',
    opacity: 0.18,
    pointerEvents: 'none',
  },
  brandHeader: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem',
  },
  brandLogo: {
    width: 56,
    height: 56,
    objectFit: 'contain',
    filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.28))',
  },
  brandName: {
    fontFamily: sharedFonts.main,
    fontSize: '1.35rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    lineHeight: 1,
  },
  brandCopy: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 460,
  },
  brandEyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    padding: '0.35rem 0.7rem',
    borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.82)',
  },
  brandTitle: {
    fontFamily: sharedFonts.main,
    fontSize: { md: '2.15rem', lg: '2.6rem' },
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: '-0.03em',
    mb: '1rem',
  },
  brandSubtitle: {
    fontSize: '1rem',
    lineHeight: 1.65,
    color: 'rgba(255,255,255,0.72)',
    maxWidth: 420,
  },
  brandPills: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.6rem',
  },
  brandPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.45rem',
    padding: '0.45rem 0.75rem',
    borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.86)',
  },
  formPanel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: { xs: '1.25rem', sm: '1.75rem', md: '2.5rem' },
  },
  mobileBrand: {
    display: { xs: 'flex', md: 'none' },
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.7rem',
    mb: '1.25rem',
  },
  mobileLogo: {
    width: 42,
    height: 42,
    objectFit: 'contain',
  },
  mobileName: {
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: authColors.text,
  },
  card: {
    width: '100%',
    maxWidth: 580,
    padding: { xs: '1.5rem', sm: '2.4rem 2.6rem' },
    borderRadius: '1.35rem',
    backgroundColor: authColors.white,
    border: `1px solid ${authColors.border}`,
    boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
  },
  cardLogo: {
    width: 72,
    height: 72,
    objectFit: 'contain',
    display: 'block',
    mx: 'auto',
    mb: '1rem',
  },
  title: {
    fontFamily: sharedFonts.main,
    fontSize: { xs: '1.5rem', sm: '1.75rem' },
    fontWeight: 700,
    letterSpacing: '-0.03em',
    color: authColors.text,
    textAlign: 'center',
    mb: '0.4rem',
  },
  subtitle: {
    fontFamily: sharedFonts.main,
    fontSize: '0.95rem',
    lineHeight: 1.55,
    color: authColors.muted,
    textAlign: 'center',
    mb: '1.6rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  field: {
    '& .MuiInputLabel-root': {
      fontFamily: sharedFonts.main,
      fontWeight: 600,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '0.85rem',
      backgroundColor: authColors.surface,
      fontFamily: sharedFonts.main,
      '& fieldset': {
        borderColor: authColors.border,
      },
      '&:hover fieldset': {
        borderColor: '#94A3B8',
      },
      '&.Mui-focused': {
        backgroundColor: authColors.white,
      },
      '&.Mui-focused fieldset': {
        borderColor: authColors.primary,
        borderWidth: 2,
      },
      '&.Mui-error fieldset': {
        borderColor: authColors.error,
      },
    },
    '& .MuiFormHelperText-root': {
      mx: 0,
      mt: '0.4rem',
      fontFamily: sharedFonts.main,
      fontSize: '0.78rem',
    },
  },
  fieldIcon: {
    color: authColors.muted,
  },
  iconButton: {
    color: authColors.muted,
    '&:hover': {
      color: authColors.text,
    },
  },
  optionsRow: {
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: '0.5rem',
    mt: '-0.2rem',
  },
  remember: {
    m: 0,
    '& .MuiFormControlLabel-label': {
      fontFamily: sharedFonts.main,
      fontSize: '0.875rem',
      fontWeight: 500,
      color: authColors.text,
    },
  },
  textLink: {
    fontFamily: sharedFonts.main,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: authColors.primary,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `2px solid ${authColors.primary}`,
      outlineOffset: 3,
      borderRadius: 4,
    },
  },
  submit: {
    mt: '0.35rem',
    py: '0.85rem',
    borderRadius: '0.85rem',
    textTransform: 'none',
    fontFamily: sharedFonts.main,
    fontSize: '0.95rem',
    fontWeight: 700,
    letterSpacing: '0.01em',
    color: authColors.white,
    backgroundColor: authColors.primary,
    boxShadow: '0 10px 20px rgba(29, 78, 216, 0.22)',
    '&:hover': {
      backgroundColor: authColors.primaryHover,
      boxShadow: '0 12px 24px rgba(29, 78, 216, 0.28)',
    },
    '&:focus-visible': {
      outline: `3px solid ${authColors.primarySoft}`,
    },
    '&.Mui-disabled': {
      color: authColors.white,
      backgroundColor: '#93C5FD',
    },
  },
  alert: {
    borderRadius: '0.85rem',
    fontFamily: sharedFonts.main,
  },
  checkbox: {
    alignItems: 'flex-start',
    m: 0,
    '& .MuiCheckbox-root': {
      pt: 0,
      color: authColors.muted,
      '&.Mui-checked': {
        color: authColors.primary,
      },
    },
    '& .MuiFormControlLabel-label': {
      fontFamily: sharedFonts.main,
      fontSize: '0.85rem',
      lineHeight: 1.5,
      color: authColors.text,
    },
  },
  footer: {
    mt: '1.35rem',
    textAlign: 'center',
    fontSize: '0.9rem',
    color: authColors.muted,
  },
  legal: {
    mt: '1.1rem',
    display: 'flex',
    justifyContent: 'center',
    gap: '1.25rem',
    flexWrap: 'wrap',
  },
  legalLink: {
    fontFamily: sharedFonts.main,
    fontSize: '0.8rem',
    color: authColors.muted,
    textDecoration: 'none',
    '&:hover': {
      color: authColors.text,
      textDecoration: 'underline',
    },
  },
};
